// Music player — plays one track from the library at a time. A track is
// either a real audio file (public/music/*.mp3) routed through the shared
// music gain, or, when no file is present, an original generative loop
// synthesised in the browser. Either way the music volume slider controls it.

import { getMusicChannel } from './audioSystem'
import { FALLBACK_GEN } from '@/config/musicLibrary'
import type { MusicTrack, GenerativeParams } from '@/config/musicLibrary'

// --- Generative engine ------------------------------------------------------
// A real 16-step-per-bar groove: drums (synthesised from oscillators + a shared
// noise buffer), a plucky bass, a hook lead and an optional soft pad, with
// swing on the offbeat 16ths. Loops indefinitely. Designed to feel like a live
// groove the instant it starts — the opposite of the old droning pad.

function midiFromScale(root: number, scale: number[], degree: number): number {
  const len = scale.length
  const octave = Math.floor(degree / len)
  const idx = ((degree % len) + len) % len
  return root + octave * 12 + scale[idx]
}

function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12)
}

// One reusable white-noise buffer per AudioContext (for the drum hits)
let noiseBuffer: AudioBuffer | null = null
let noiseCtx: AudioContext | null = null
function getNoise(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer && noiseCtx === ctx) return noiseBuffer
  const len = Math.floor(ctx.sampleRate)
  const buf = ctx.createBuffer(1, len, ctx.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1
  noiseBuffer = buf
  noiseCtx = ctx
  return buf
}

class GenerativeBand {
  private timer: ReturnType<typeof setInterval> | null = null
  private nextStepTime = 0
  private step = 0
  private live: AudioScheduledSourceNode[] = []

  constructor(
    private ctx: AudioContext,
    private out: GainNode,
    private p: GenerativeParams,
  ) {}

  start() {
    this.nextStepTime = this.ctx.currentTime + 0.12
    this.step = 0
    this.timer = setInterval(() => this.scheduler(), 25)
  }

  stop() {
    if (this.timer) { clearInterval(this.timer); this.timer = null }
    for (const n of this.live) { try { n.stop() } catch { /* already stopped */ } }
    this.live = []
  }

  private scheduler() {
    const stepDur = (60 / this.p.bpm) / 4 // 16th notes
    // If we fell far behind (e.g. the tab was backgrounded and setInterval was
    // throttled), resync instead of scheduling a burst of past-due notes.
    if (this.nextStepTime < this.ctx.currentTime - 0.1) {
      this.nextStepTime = this.ctx.currentTime + 0.05
    }
    while (this.nextStepTime < this.ctx.currentTime + 0.25) {
      this.scheduleStep(this.step, this.nextStepTime, stepDur)
      this.step++
      this.nextStepTime += stepDur
    }
  }

  private scheduleStep(globalStep: number, baseTime: number, stepDur: number) {
    const i = globalStep % 16
    const bar = Math.floor(globalStep / 16)
    const chordDeg = this.p.progression[bar % this.p.progression.length]
    // Swing: push the offbeat 16ths later so the whole groove shuffles together
    const t = baseTime + (i % 2 === 1 ? this.p.swing * stepDur : 0)

    if (this.p.kick[i]) this.kick(t)
    if (this.p.snare[i]) this.snare(t)
    if (this.p.hat[i]) this.hat(t, i % 4 === 0)

    const b = this.p.bass[i]
    if (b !== -99) this.bass(midiFromScale(this.p.root - 12, this.p.scale, chordDeg + b), t, stepDur * 0.7)

    const l = this.p.lead[i]
    if (l !== -99) this.lead(midiFromScale(this.p.root + 12, this.p.scale, chordDeg + l), t, stepDur * 1.4)

    if (this.p.pad && i === 0) {
      const barDur = stepDur * 16
      // 7th chord (root/3rd/5th/7th) — the lush, jazzy pad at the heart of deep house
      for (const d of [0, 2, 4, 6]) {
        this.pad(midiFromScale(this.p.root, this.p.scale, chordDeg + d), baseTime, barDur)
      }
    }
  }

  private track(node: AudioScheduledSourceNode) {
    this.live.push(node)
    node.onended = () => { this.live = this.live.filter(n => n !== node) }
  }

  private kick(t: number) {
    const o = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    o.type = 'sine'
    o.frequency.setValueAtTime(150, t)
    o.frequency.exponentialRampToValueAtTime(45, t + 0.12)
    g.gain.setValueAtTime(0.0001, t)
    g.gain.linearRampToValueAtTime(0.6, t + 0.005)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18)
    o.connect(g); g.connect(this.out)
    o.start(t); o.stop(t + 0.22); this.track(o)
  }

  private snare(t: number) {
    const n = this.ctx.createBufferSource()
    n.buffer = getNoise(this.ctx)
    const hp = this.ctx.createBiquadFilter()
    hp.type = 'highpass'; hp.frequency.value = 1200
    const ng = this.ctx.createGain()
    ng.gain.setValueAtTime(0.32, t)
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.15)
    n.connect(hp); hp.connect(ng); ng.connect(this.out)
    n.start(t); n.stop(t + 0.16); this.track(n)

    const o = this.ctx.createOscillator()
    const og = this.ctx.createGain()
    o.type = 'triangle'; o.frequency.setValueAtTime(180, t)
    og.gain.setValueAtTime(0.12, t)
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.1)
    o.connect(og); og.connect(this.out)
    o.start(t); o.stop(t + 0.12); this.track(o)
  }

  private hat(t: number, accent: boolean) {
    const n = this.ctx.createBufferSource()
    n.buffer = getNoise(this.ctx)
    const hp = this.ctx.createBiquadFilter()
    hp.type = 'highpass'; hp.frequency.value = 7000
    const g = this.ctx.createGain()
    g.gain.setValueAtTime(accent ? 0.14 : 0.08, t)
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.04)
    n.connect(hp); hp.connect(g); g.connect(this.out)
    n.start(t); n.stop(t + 0.05); this.track(n)
  }

  private bass(midi: number, t: number, dur: number) {
    const o = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    const lp = this.ctx.createBiquadFilter()
    o.type = this.p.timbres.bass
    o.frequency.setValueAtTime(midiToFreq(midi), t)
    lp.type = 'lowpass'; lp.frequency.value = 750
    const d = Math.min(dur, 0.18)
    g.gain.setValueAtTime(0.0001, t)
    g.gain.linearRampToValueAtTime(0.22, t + 0.005)
    g.gain.exponentialRampToValueAtTime(0.0001, t + d)
    o.connect(lp); lp.connect(g); g.connect(this.out)
    o.start(t); o.stop(t + d + 0.03); this.track(o)
  }

  private lead(midi: number, t: number, dur: number) {
    const freq = midiToFreq(midi)
    const lp = this.ctx.createBiquadFilter()
    lp.type = 'lowpass'; lp.frequency.value = 3000; lp.Q.value = 3
    const g = this.ctx.createGain()
    const d = Math.min(dur, 0.3)
    g.gain.setValueAtTime(0.0001, t)
    g.gain.linearRampToValueAtTime(0.14, t + 0.01)
    g.gain.exponentialRampToValueAtTime(0.0001, t + d)
    lp.connect(g); g.connect(this.out)
    // Two detuned oscillators for shimmer/width
    for (const detune of [0, 6]) {
      const o = this.ctx.createOscillator()
      o.type = this.p.timbres.lead
      o.frequency.setValueAtTime(freq, t)
      o.detune.setValueAtTime(detune, t)
      o.connect(lp)
      o.start(t); o.stop(t + d + 0.05); this.track(o)
    }
  }

  private pad(midi: number, t: number, dur: number) {
    const o = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    const lp = this.ctx.createBiquadFilter()
    o.type = this.p.timbres.pad
    o.frequency.setValueAtTime(midiToFreq(midi), t)
    // Warm, mellow pad (deep house leans on the pad, kept under the groove)
    lp.type = 'lowpass'; lp.frequency.value = 1300
    g.gain.setValueAtTime(0.0001, t)
    g.gain.linearRampToValueAtTime(0.035, t + 0.4)
    g.gain.setValueAtTime(0.035, t + Math.max(0.4, dur - 0.4))
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)
    o.connect(lp); lp.connect(g); g.connect(this.out)
    o.start(t); o.stop(t + dur + 0.05); this.track(o)
  }
}

// --- Player -----------------------------------------------------------------

type Channel = { ctx: AudioContext; gain: GainNode }

let voice: GenerativeBand | null = null
let audioEl: HTMLAudioElement | null = null
let mediaSource: MediaElementAudioSourceNode | null = null
let activeTrackId: string | null = null

export function stopMusicPlayer() {
  voice?.stop()
  voice = null
  if (audioEl) {
    audioEl.pause()
    audioEl.removeAttribute('src')
    audioEl = null
  }
  if (mediaSource) {
    try { mediaSource.disconnect() } catch { /* noop */ }
    mediaSource = null
  }
  activeTrackId = null
}

function startGenerative(track: MusicTrack, ch: Channel) {
  if (activeTrackId !== track.id) return
  voice = new GenerativeBand(ch.ctx, ch.gain, track.gen ?? FALLBACK_GEN)
  voice.start()
}

function startFile(track: MusicTrack, ch: Channel, src: string) {
  const el = new Audio()
  el.loop = true
  el.preload = 'auto'
  let started = false

  el.addEventListener('canplay', () => {
    // Bail if the track was switched/stopped while the file was buffering
    if (started || activeTrackId !== track.id) return
    started = true
    try {
      mediaSource = ch.ctx.createMediaElementSource(el)
      mediaSource.connect(ch.gain)
    } catch { /* already connected or unsupported */ }
    el.play().catch(() => { /* autoplay blocked until user gesture */ })
  }, { once: true })

  el.addEventListener('error', () => {
    // No real file present (or blocked) — fall back to the generative loop.
    // Skip if playback already started or the track was switched.
    if (started || activeTrackId !== track.id) return
    if (audioEl === el) audioEl = null
    if (mediaSource) { try { mediaSource.disconnect() } catch { /* noop */ }; mediaSource = null }
    startGenerative(track, ch)
  }, { once: true })

  audioEl = el
  el.src = src
  el.load()
}

export function playTrack(track: MusicTrack) {
  stopMusicPlayer()
  const ch = getMusicChannel()
  if (!ch) return
  activeTrackId = track.id

  const begin = () => {
    if (activeTrackId !== track.id) return
    if (track.src) startFile(track, ch, track.src)
    else startGenerative(track, ch)
  }

  // Resume the context within the user gesture, then begin once it's running
  // so the first scheduled notes aren't dropped on a still-suspended context.
  if (ch.ctx.state === 'suspended') {
    ch.ctx.resume().then(begin).catch(begin)
  } else {
    begin()
  }
}
